import React, { Component } from "react";
import ClearFilterButton from "../../components/ClearFilterButton.js";
import {
  loading,
  NavbarCustomer,
  Pagination,
  Select,
  GET,
  t,
} from "../../components/CustomComponent.js";
import {
  fill,
  fmtInt,
  fmtDate,
  fmtDay,
  StatusBadge,
  WarrantyBadge,
  OpenBadge,
  EmptyBox,
  Skeleton,
  ErrorCard,
} from "../../components/AssetHistoryParts.js";
import { pruneParams } from "../../utils/timeTracking.js";
import "../../assets/css/asset-history.css";
import { formatNumber } from "../../utils/formatting";

const PAGE_SIZE = 10;
const Q_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const ACCESS_DENIED = "Access denied";

// ประวัติทะเบียนสินทรัพย์ฝั่งลูกค้า (แบบย่อ) - GET customer/asset-history · ไม่มีต้นทุน/BU/Sales ตาม API
// คลิกแถว -> /cs/asset-history-detail?uid=
export default class CustomerAssetReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      q: "",
      status: "",
      warranty: "",
      sort_by: "",
      sort_dir: "desc",
      page: 1,
      rows: [],
      count: 0,
      loaded: false,
      fetching: true,
      error: "",
      forbidden: false,
    };
    this.q_timer = null;
    this.req_id = 0;
    this.last_search = "";
  }

  componentDidMount() {
    loading(false);
    this.Get(1);
  }

  componentWillUnmount() {
    clearTimeout(this.q_timer);
  }

  effectiveQ = (q) => (q.trim().length >= MIN_SEARCH_LENGTH ? q.trim() : "");

  buildParams = () => {
    const { q, status, warranty, sort_by, sort_dir } = this.state;
    return pruneParams({ q: this.effectiveQ(q), status, warranty, sort_by, sort_dir: sort_by ? sort_dir : "" });
  };

  Get = async (page = 1) => {
    const req = ++this.req_id;
    this.setState({ fetching: true, error: "", forbidden: false });
    const result = await GET("customer/asset-history", { ...this.buildParams(), page, limit: PAGE_SIZE }, true, { skipAccessDeniedAlert: true });
    if (req !== this.req_id) return;
    if (result?.status && result.data) {
      const rows = result.data.rows || [];
      this.setState({ rows, count: result.pagination?.count ?? rows.length, page, loaded: true, fetching: false });
      return;
    }
    const message = typeof result?.message === "string" ? result.message : "";
    this.setState({ rows: [], count: 0, page: 1, loaded: true, fetching: false, forbidden: message === ACCESS_DENIED, error: message === ACCESS_DENIED ? "" : message || t("ah_error_hint") });
  };

  SetFilter = (patch) => this.setState(patch, () => this.Get(1));

  SetQ = (value) => {
    this.setState({ q: value });
    clearTimeout(this.q_timer);
    this.q_timer = setTimeout(() => {
      const next = this.effectiveQ(this.state.q);
      if (next === this.last_search) return;
      this.last_search = next;
      this.Get(1);
    }, Q_DEBOUNCE_MS);
  };

  ResetFilter = () => {
    clearTimeout(this.q_timer);
    this.last_search = "";
    this.SetFilter({ q: "", status: "", warranty: "", sort_by: "", sort_dir: "desc" });
  };

  SortBy = (field) => {
    const { sort_by, sort_dir } = this.state;
    this.SetFilter({ sort_by: field, sort_dir: sort_by === field && sort_dir === "asc" ? "desc" : "asc" });
  };

  GoDetail = (uid) => {
    window.location.href = `/cs/asset-history-detail?uid=${encodeURIComponent(uid)}`;
  };

  renderSortTh = (field, label, className = "") => {
    const { sort_by, sort_dir } = this.state;
    return (
      <th className={`border-0 ah-th-sort ${className}`} onClick={() => this.SortBy(field)}>
        {label} {sort_by === field ? (sort_dir === "asc" ? "▲" : "▼") : ""}
      </th>
    );
  };

  renderTable() {
    const { rows } = this.state;
    const th = (label, cls = "") => <th className={`border-0 ${cls}`}>{label}</th>;
    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0 ah-table" data-testid="ah-cs-table">
          <thead>
            <tr className="text-dark fw-bold border-bottom border-light">
              {this.renderSortTh("asset_code", t("ah_col_asset_id"), "nw")}
              {th(t("ah_col_product"))}
              {th(t("ah_col_serial"), "nw")}
              {th(t("ah_col_location"))}
              {th(t("ah_col_project"))}
              {th(t("ah_col_status"), "nw")}
              {this.renderSortTh("warranty_expire", t("ah_col_warranty_latest"), "nw")}
              {this.renderSortTh("last_repair_at", t("ah_col_last_repair"), "nw")}
              {th(t("ah_col_repairs"), "nw num")}
            </tr>
          </thead>
          <tbody>
            {!rows.length ? (
              <tr>
                <td colSpan={9}>
                  <EmptyBox icon="🔍" title={t("ah_list_empty")} hint={t("ah_list_empty_hint")}>
                    <ClearFilterButton onClick={this.ResetFilter} />
                  </EmptyBox>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.uid} className="border-bottom border-light bg-white ah-row" onClick={() => this.GoDetail(r.uid)} data-testid="ah-cs-row">
                  <td className="ref">{r.asset_code}</td>
                  <td className="ah-prod wrap"><b>{r.product_name || "-"}</b><span>{[r.brand_name, r.part_number].filter(Boolean).join(" · ")}</span></td>
                  <td className="nw">{r.serial_number || "-"}</td>
                  <td className="wrap">{[r.location_name, r.location_province].filter(Boolean).join(" · ") || "-"}</td>
                  <td className="wrap sub">{r.project_name || "-"}</td>
                  <td className="nw"><StatusBadge status={r.status} /></td>
                  <td className="nw"><WarrantyBadge warranty={r.warranty} /><div className="sub">{r.warranty?.expire_date ? fill(t("ah_expire_on"), { d: fmtDate(r.warranty.expire_date) }) : ""}</div></td>
                  <td className="nw">
                    {r.last_repair_at ? fmtDay(r.last_repair_at) : <span className="sub">{t("ah_never_repaired")}</span>}
                    {r.has_open_work_order ? <div><OpenBadge /></div> : null}
                  </td>
                  <td className="num nw fw-bold">{fmtInt(r.repair_count)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  }

  renderResults() {
    const { rows, count, page, loaded, fetching, error, forbidden } = this.state;
    if (forbidden) {
      return (
        <div className="bg-white rounded-4 shadow-sm p-5 text-center" data-testid="ah-forbidden">
          <h4 className="fw-bold text-dark">{t("ah_forbidden")}</h4>
        </div>
      );
    }
    if (error) return <ErrorCard message={error} onRetry={() => this.Get(page)} />;
    if (!loaded) return <Skeleton heights={[60, 320]} />;
    const from = count ? (page - 1) * PAGE_SIZE + 1 : 0;
    const to = count ? from + rows.length - 1 : 0;
    return (
      <>
        <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden" style={fetching ? { opacity: 0.55 } : undefined}>
          <div className="px-4 pt-3 pb-2 text-12 text-secondary" data-testid="ah-result-count">{count ? fill(t("ah_found"), { n: fmtInt(count), from: formatNumber(from), to: formatNumber(to) }) : ""}</div>
          {this.renderTable()}
        </div>
        <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap text-secondary text-12">
          <div>{t("showing")} {formatNumber(from)}-{formatNumber(to)} {t("of")} {formatNumber(count)}</div>
          <Pagination className="mb-0" count={Math.ceil(count / PAGE_SIZE) || 1} page={page} showFirstButton={false} showLastButton={false} onChange={(_, next) => this.Get(next)} />
        </div>
      </>
    );
  }

  render() {
    const { q, status, warranty } = this.state;
    const short = q.trim().length > 0 && q.trim().length < MIN_SEARCH_LENGTH;
    return (
      <div className="w-100 min-vh-100 pb-5" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarCustomer />
        <div className="body px-4 mt-3">
          <div className="mb-4">
            <h3 className="fw-bold mb-1 text-dark">{t("asset_report")}</h3>
            <div className="text-secondary text-14">{t("ah_cs_desc")}</div>
          </div>

          <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-6 col-xl-5">
                <label className="form-label text-12 text-secondary mb-1">{t("ah_f_search")}</label>
                <input type="text" className="form-control" value={q} placeholder={t("ah_cs_search_ph")} onChange={(e) => this.SetQ(e.target.value)} data-testid="ah-f-search" />
                {short ? <div className="text-12 text-secondary mt-1">{fill(t("ah_search_min"), { n: MIN_SEARCH_LENGTH })}</div> : null}
              </div>
              <div className="col-md-3 col-xl-2" data-testid="ah-f-status">
                <label className="form-label text-12 text-secondary mb-1">{t("ah_f_status")}</label>
                <Select
                  className="form-control w-100"
                  options={[{ value: "", label: t("ah_all_status") }, { value: "active", label: t("ah_s_active") }, { value: "inactive", label: t("ah_s_inactive") }]}
                  value={status}
                  onChange={(v) => this.SetFilter({ status: v || "" })}
                  placeholder={t("ah_all_status")}
                />
              </div>
              <div className="col-md-3 col-xl-2" data-testid="ah-f-warranty">
                <label className="form-label text-12 text-secondary mb-1">{t("ah_f_warranty")}</label>
                <Select
                  className="form-control w-100"
                  options={[{ value: "", label: t("ah_all_warranty") }, { value: "in", label: t("ah_w_in") }, { value: "near", label: t("ah_f_w_near") }, { value: "expired", label: t("ah_w_expired") }, { value: "none", label: t("ah_f_w_none") }]}
                  value={warranty}
                  onChange={(v) => this.SetFilter({ warranty: v || "" })}
                  placeholder={t("ah_all_warranty")}
                />
              </div>
              <div className="col-auto">
                <ClearFilterButton onClick={this.ResetFilter} />
              </div>
            </div>
          </div>

          {this.renderResults()}
        </div>
      </div>
    );
  }
}
